import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Sparkles, UploadCloud, Loader2, AlertTriangle, Trash2, CheckCircle2, PackagePlus, Factory } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiErrorDetail } from "@/lib/api";
import { logger } from "@/lib/logger";
import { typeLabel } from "@/lib/itemTypes";

const yellow = "border-yellow-400 bg-yellow-50 focus-visible:ring-yellow-300";
const NEW_TYPES = ["raw", "spare", "manufactured", "mfg_spare"].map((v) => ({ value: v, label: typeLabel(v) }));

export default function AiBomImportDialog({ onClose, facility = "H. K. INDUSTRIES", onImported }) {
  const [machines, setMachines] = useState([]);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    api.get("/machines").then((m) => setMachines(m || [])).catch((e) => logger.error("machines load failed:", e));
    api.get("/bom/items").then((m) => setCatalog(m || [])).catch((e) => logger.error("catalog load failed:", e));
  }, []);
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const [fgName, setFgName] = useState("");
  const [fgSku, setFgSku] = useState("");
  const [fgUncertain, setFgUncertain] = useState(false);
  const [existingFg, setExistingFg] = useState(null); // {id, name, sku}
  const [replaceRecipe, setReplaceRecipe] = useState(false);
  const [lineId, setLineId] = useState("");
  const [suggestedLine, setSuggestedLine] = useState(null); // {id, name}
  const [rows, setRows] = useState([]);

  const extract = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const csrf = (document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/) || [])[1] || "";
      const res = await fetch(`${API}/bom/ai-import`, { method: "POST", credentials: "include", headers: { "X-CSRF-Token": decodeURIComponent(csrf) }, body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data?.detail === "string" ? data.detail : "Extraction failed");
      const fg = data.finished_good || {};
      setFgName(fg.name || "");
      setFgSku(fg.sku || "");
      setFgUncertain(!!fg.uncertain);
      setExistingFg(fg.existing_fg_id ? { id: fg.existing_fg_id, name: fg.existing_fg_name, sku: fg.existing_fg_sku } : null);
      setReplaceRecipe(false);
      const sug = data.suggested_line_id ? { id: data.suggested_line_id, name: data.suggested_line_name } : null;
      setSuggestedLine(sug);
      setLineId(sug?.id || "");
      setRows((data.components || []).map((c, i) => ({
        key: `r${i}`,
        raw_name: c.name || "",
        source: c.source || null,
        picking: false,
        name: c.name || "",
        sku: c.sku || "",
        quantity: c.quantity != null ? String(c.quantity) : "",
        unit: c.unit || "pcs",
        matched: !!c.matched,
        item_id: c.item_id || "",
        matched_sku: c.matched_sku || "",
        matched_name: c.matched_name || "",
        matched_type: c.matched_type || "",
        is_new: !!c.is_new,
        new_type: "raw",
        create: !!c.is_new, // New Items default to auto-create
        uncertain: !!c.uncertain,
      })));
      setStep("review");
      const newCount = (data.components || []).filter((c) => c.is_new).length;
      toast.success(`BOM read — ${data.components?.length || 0} material(s) found${newCount ? `, ${newCount} new` : ""}`);
    } catch (e) {
      toast.error(e.message || "Could not read the sheet");
    } finally {
      setBusy(false);
    }
  };

  const setRow = (i, patch) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch, uncertain: false } : r)));
  const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

  const valid = fgName.trim() && fgSku.trim() && rows.length > 0 &&
    rows.every((r) => Number(r.quantity) > 0 && (r.matched ? !!r.item_id : (r.create ? !!r.name.trim() : true))) &&
    (!existingFg || replaceRecipe);

  const confirm = async () => {
    setSaving(true);
    try {
      const res = await api.post("/bom/ai-import/confirm", {
        fg_name: fgName.trim(),
        fg_sku: fgSku.trim(),
        fg_unit: "pcs",
        line_id: lineId || null,
        existing_fg_id: existingFg?.id || null,
        replace_recipe: replaceRecipe,
        tenant_id: facility,
        components: rows.map((r) => ({
          item_id: r.matched ? r.item_id : null,
          create_new: !r.matched && r.create,
          name: r.name.trim(),
          sheet_name: r.raw_name || r.name.trim(),
          sku: r.sku.trim() || null,
          item_type: r.new_type,
          unit: r.unit || "pcs",
          quantity_per_unit: Number(r.quantity),
        })),
      });
      const made = res.created_items?.length ? ` · ${res.created_items.length} new item(s) created` : "";
      toast.success(res.replaced ? `Recipe replaced on ${res.fg_name}${made}` : `Finished good ${res.fg_name} created with ${res.component_count} material(s)${made}`);
      onImported?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="ai-bom-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" /> AI BOM Import</DialogTitle>
          <DialogDescription className="text-xs">
            Upload a single-product sheet (.xlsx / .csv) with the Finished Good Name &amp; SKU at the top and the material rows below.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <button type="button" onClick={() => inputRef.current?.click()}
              className="w-full border-2 border-dashed border-slate-300 rounded-xl py-10 flex flex-col items-center gap-2 text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
              data-testid="ai-bom-dropzone">
              <UploadCloud className="h-8 w-8" />
              <span className="text-sm font-medium">{file ? file.name : "Click to choose a BOM sheet"}</span>
              <span className="text-[11px] text-slate-400">.xlsx or .csv · single product · FG name &amp; SKU on top</span>
            </button>
            <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ai-bom-file-input" />
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={!file || busy} onClick={extract} data-testid="ai-bom-extract-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                {busy ? "Reading…" : "Extract with AI"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="ai-bom-fg-card">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Factory className="h-3.5 w-3.5" /> Finished Good
                {fgUncertain && <span className="text-yellow-600 normal-case font-normal flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> please verify</span>}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input value={fgName} onChange={(e) => { setFgName(e.target.value); setFgUncertain(false); }} className={fgUncertain ? yellow : ""} data-testid="ai-bom-fg-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">SKU</Label>
                  <Input value={fgSku} onChange={(e) => { setFgSku(e.target.value); setFgUncertain(false); }} className={fgUncertain ? yellow : ""} data-testid="ai-bom-fg-sku" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Production Line {suggestedLine && <span className="text-blue-600 font-normal">· suggested from product name</span>}</Label>
                <Select value={lineId || "__none__"} onValueChange={(v) => setLineId(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="h-9" data-testid="ai-bom-line-select"><SelectValue placeholder="No line assigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No line assigned</SelectItem>
                    {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.line_code ? ` (${m.line_code})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {existingFg && (
                <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="ai-bom-existing-fg">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span className="flex-1">
                    <b>{existingFg.name}</b> ({existingFg.sku}) already exists in the catalog.
                    <span className="flex items-center gap-1.5 mt-1">
                      <input type="checkbox" checked={replaceRecipe} onChange={(e) => setReplaceRecipe(e.target.checked)} className="h-3.5 w-3.5 accent-blue-600" data-testid="ai-bom-replace-recipe" />
                      Replace its recipe with this imported BOM
                    </span>
                  </span>
                </label>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="ai-bom-materials-table">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                Materials ({rows.length}) — {rows.filter((r) => r.is_new).length} new
              </div>
              <div className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <div key={r.key} className="px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`ai-bom-row-${i}`}>
                    <div className="min-w-0 flex-1">
                      {r.matched ? (
                        <>
                          {r.picking ? (
                            <SearchableSelect
                              value=""
                              onValueChange={(v) => {
                                const it = catalog.find((x) => x.id === v);
                                if (it) setRow(i, { item_id: it.id, matched_name: it.name, matched_sku: it.sku, matched_type: it.item_type, unit: it.unit || r.unit, source: "manual", picking: false });
                              }}
                              options={catalog.map((x) => ({ value: x.id, label: `${x.name} (${x.sku})`, keywords: `${x.name} ${x.sku}` }))}
                              placeholder="Pick a catalog item…"
                              searchPlaceholder="Search materials…"
                              testid={`ai-bom-rematch-${i}`}
                            />
                          ) : (
                            <>
                              <p className="text-xs font-semibold text-slate-800 truncate flex items-center gap-1.5 flex-wrap">
                                {r.matched_name}
                                <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-2.5 w-2.5" /> In catalog</span>
                                {r.source === "alias" && (
                                  <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700" data-testid={`ai-bom-learned-${i}`}><Sparkles className="h-2.5 w-2.5" /> Learned</span>
                                )}
                              </p>
                              <p className="text-[10px] text-slate-400 font-mono-data">
                                {r.matched_sku}{r.matched_name !== r.raw_name ? ` · sheet says “${r.raw_name}”` : ""}
                                {" · "}
                                <button type="button" className="text-blue-600 hover:underline" onClick={() => setRow(i, { picking: true })} data-testid={`ai-bom-change-match-${i}`}>change match</button>
                              </p>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5">
                            <Input value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} className={`h-7 text-xs flex-1 ${r.uncertain ? yellow : ""}`} placeholder="Material name" data-testid={`ai-bom-new-name-${i}`} />
                            <Input value={r.sku} onChange={(e) => setRow(i, { sku: e.target.value })} className="h-7 text-xs w-28 font-mono-data" placeholder="SKU (auto)" data-testid={`ai-bom-new-sku-${i}`} />
                          </div>
                          <p className="text-[10px] mt-1 flex items-center gap-1.5 flex-wrap">
                            <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700" data-testid={`ai-bom-new-badge-${i}`}><PackagePlus className="h-2.5 w-2.5" /> New Item</span>
                            <label className="inline-flex items-center gap-1 text-slate-500 cursor-pointer">
                              <input type="checkbox" checked={r.create} onChange={(e) => setRow(i, { create: e.target.checked })} className="h-3 w-3 accent-blue-600" data-testid={`ai-bom-new-create-${i}`} />
                              auto-create
                            </label>
                            <Select value={r.new_type} onValueChange={(v) => setRow(i, { new_type: v })}>
                              <SelectTrigger className="h-6 w-36 text-[10px]" data-testid={`ai-bom-new-type-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{NEW_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </p>
                        </>
                      )}
                    </div>
                    <Input type="number" min="0" step="any" value={r.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} className={`h-7 text-xs w-20 text-right font-mono-data ${r.uncertain ? yellow : ""}`} placeholder="Qty" data-testid={`ai-bom-qty-${i}`} />
                    <Input value={r.unit} onChange={(e) => setRow(i, { unit: e.target.value })} className="h-7 text-xs w-16" placeholder="unit" data-testid={`ai-bom-unit-${i}`} />
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-rose-600" onClick={() => removeRow(i)} data-testid={`ai-bom-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("upload")} data-testid="ai-bom-back-btn">Back</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={!valid || saving} onClick={confirm} data-testid="ai-bom-confirm-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                {saving ? "Creating…" : existingFg ? "Replace Recipe" : "Create BOM"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
